import React, { useState } from 'react';
import { CalendarDays, Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { useAdminEvent, AdminEvent } from '@/contexts/AdminEventContext';

function formatRange(event: AdminEvent): string {
  if (!event.start_date) return '';
  const fmt = (d: string) => new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  if (event.end_date && event.end_date !== event.start_date) {
    return `${fmt(event.start_date)} – ${fmt(event.end_date)}`;
  }
  return fmt(event.start_date);
}

export function AdminEventSwitcher({ compact = false }: { compact?: boolean }) {
  const { loading, selectedEvent, selectedEventId, setSelectedEventId, upcomingEvents, pastEvents } = useAdminEvent();
  const [open, setOpen] = useState(false);

  if (loading) {
    return <div className={cn('rounded-xl bg-muted animate-pulse', compact ? 'h-12 w-full' : 'h-14 w-72')} />;
  }
  if (!selectedEvent) return null;

  const renderItem = (event: AdminEvent) => (
    <CommandItem
      key={event.id}
      value={`${event.name} ${event.id}`}
      onSelect={() => {
        setSelectedEventId(event.id);
        setOpen(false);
      }}
    >
      <Check className={cn('mr-2 h-4 w-4 shrink-0', event.id === selectedEventId ? 'opacity-100' : 'opacity-0')} />
      <div className="min-w-0">
        <div className="truncate">{event.name}</div>
        {event.start_date && (
          <div className="text-xs text-muted-foreground">{formatRange(event)}</div>
        )}
      </div>
    </CommandItem>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label="Switch event"
          className={cn(
            'h-auto justify-between rounded-xl border-primary/30 bg-primary/5 hover:bg-primary/10',
            compact ? 'w-full gap-2 px-2.5 py-2' : 'w-full sm:w-auto gap-3 px-4 py-2.5'
          )}
        >
          <div className={cn('flex items-center min-w-0', compact ? 'gap-2' : 'gap-3')}>
            <div className={cn('rounded-lg bg-primary/15 text-primary shrink-0', compact ? 'p-1.5' : 'p-2')}>
              <CalendarDays className={compact ? 'h-4 w-4' : 'h-5 w-5'} />
            </div>
            <div className="text-left min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Working on</div>
              <div className={cn('font-semibold truncate', compact ? 'text-sm max-w-[10.5rem]' : 'max-w-[16rem]')}>
                {selectedEvent.name}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground shrink-0">
            {!compact && <span className="text-xs hidden sm:inline">Switch event</span>}
            <ChevronsUpDown className="h-4 w-4" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search events..." />
          <CommandList>
            <CommandEmpty>No event found.</CommandEmpty>
            {upcomingEvents.length > 0 && (
              <CommandGroup heading="Upcoming & Active">
                {upcomingEvents.map(renderItem)}
              </CommandGroup>
            )}
            {upcomingEvents.length > 0 && pastEvents.length > 0 && <CommandSeparator />}
            {pastEvents.length > 0 && (
              <CommandGroup heading="Past events">
                {pastEvents.map(renderItem)}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
